<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { Color } from '@previo/codebooks'
import { $t } from '@previo/core'
import {
  PDateRangePickerFastChoice,
  type PDateRangePickerFastChoiceItem,
} from '../../../date-range-picker/types'
import PSelectInputField from '../../../fields/PSelectInputField.vue'
import PTextFormBody from '../../../typography/form/PTextFormBody.vue'
import { usePrevioDS } from '../../../../composables/helpers'
import { useDateTimeRangePicker } from '../../composables/DateTimeRangePickerState'
import { termChoicesList } from '../../../date-range-picker/constants'
import { DateTimeRange } from '../../model/DateTimeRange'
import { applyTimeToDate } from '../../utils'
import { resolveDateRangeFastChoice } from './resolveDateRangeFastChoice'

type Props = {
  fastChoices?: PDateRangePickerFastChoice[]
  defaultTimeFrom?: string
  defaultTimeTo?: string
}

const props = defineProps<Props>()
const model = defineModel<DateTimeRange>({ default: new DateTimeRange() })

const ds = usePrevioDS()
const navigation = useDateTimeRangePicker()

const selectedFastChoice = ref<PDateRangePickerFastChoice>()
const visibleFastChoices = computed<PDateRangePickerFastChoiceItem[]>(() => {
  const visible: PDateRangePickerFastChoiceItem[] = []

  props.fastChoices?.map((choiceType) => {
    const choice = termChoicesList.find((choice) => choice.type === choiceType)

    if (choice) {
      visible.push(choice)
    }
  })

  return visible
})

const hasFastChoice = computed<boolean>(() => {
  return visibleFastChoices.value.length > 0
})

watch(selectedFastChoice, (value) => {
  if (!value) {
    return
  }

  const { from, to } = resolveDateRangeFastChoice(value)

  model.value.from = applyTimeToDate(from, props.defaultTimeFrom)
  model.value.to = applyTimeToDate(to, props.defaultTimeTo)

  navigation.setRange(
    new DateTimeRange(
      applyTimeToDate(from, props.defaultTimeFrom),
      applyTimeToDate(to, props.defaultTimeTo),
    ),
  )
})
</script>

<template>
  <p-select-input-field
    v-if="hasFastChoice && ds.screen().isMobile"
    v-model="selectedFastChoice"
    :items="visibleFastChoices"
    :placeholder="$t('Choice fast filter')"
    item-value="type"
    item-text="name"
    cy="fast-choice-select"
  >
    <template #item="{ item }: { item: PDateRangePickerFastChoiceItem }">
      <p-text-form-body
        :text="$t(item.name)"
        :color="selectedFastChoice === item.type ? Color.PRIMARY : undefined"
      />
    </template>
  </p-select-input-field>
</template>
